import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/cn';

const baseFieldStyles = cn(
  'w-full px-3 py-2 text-base text-ink-primary bg-white',
  'border border-line rounded',
  'placeholder:text-ink-quaternary',
  'transition-all duration-fast',
  'focus:outline-none focus:border-brand-orange focus:ring-[3px] focus:ring-brand-orange/15',
  'disabled:opacity-50 disabled:cursor-not-allowed',
);

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ leftIcon, className, ...props }, ref) => {
    if (leftIcon) {
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-quaternary pointer-events-none">
            {leftIcon}
          </span>
          <input
            ref={ref}
            className={cn(baseFieldStyles, 'pl-10', className)}
            {...props}
          />
        </div>
      );
    }
    return <input ref={ref} className={cn(baseFieldStyles, className)} {...props} />;
  },
);
Input.displayName = 'Input';

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, ...props }, ref) => (
  <select ref={ref} className={cn(baseFieldStyles, 'pr-8', className)} {...props}>
    {children}
  </select>
));
Select.displayName = 'Select';

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(baseFieldStyles, 'min-h-[80px] resize-y', className)}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

interface LabelProps {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Label({ htmlFor, required, children, className }: LabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('block text-sm font-medium text-ink-secondary mb-1', className)}
    >
      {children}
      {required && <span className="text-danger ml-1">*</span>}
    </label>
  );
}
